import React from 'react';

import type { DynamicBlockElement, NonSigFieldElement, Trigger } from '@aline/common';
import { isDynamicBlockElement, variableVariantToFieldVariant } from '@aline/common';
import { link, params } from '@aline/routes';
import { IconButton, Typography } from '@mui/material';
import { ChevronDown, ChevronUp, Copy, Eye, SquarePen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { NodeEntry } from 'slate';
import { Path } from 'slate';
import { AlineTooltip, FieldIcon, getVariableVariantDisplayString, Spacer } from 'src/components';
import { IconCircle } from 'src/components/icons/IconCircle';
import { setVariablePanelFocusedField } from 'src/components/variables/document-variables/variable-panel-state-store';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  useEditorTabStateSelector,
  useGetFieldsInJSON,
} from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { getPathWithDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { ClientTransforms } from 'src/editor/transforms';
import type { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack, useDocumentParams } from 'src/helpers';
import { CrmIntegrationIconWithTooltip } from 'src/images/integrations';
import { useEditor } from 'src/layout-engine';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  buttons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  icon: {
    height: 18,
    width: 18,
  },
  moveButtons: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    borderRadius: 3,
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.5),
  },
  section: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  spacer: {
    minWidth: theme.spacing(5),
  },
  wrapper: {
    alignItems: 'center',
    borderRadius: 3,
    display: 'flex',
    padding: theme.spacing(1, 1.5),
  },
}));

type AnswerOptionSubset = Pick<
  Apollo.AnswerOption,
  '__typename' | 'id' | 'optionText' | 'triggerAction' | 'choiceRefId'
>;
interface AnswerOptionsSubset extends Pick<Apollo.AnswerOptions, '__typename' | 'id'> {
  options: AnswerOptionSubset[];
}

interface VariablePopoverVariable extends Pick<
  Apollo.DocumentVariable,
  'id' | 'label' | 'nodeId' | '__typename' | 'variant'
> {
  answerOptions: AnswerOptionsSubset;
  integrationLink: Pick<
    Apollo.IntegrationLink,
    'attributePath' | 'integration' | '__typename'
  > | null;
}

interface IncompleteVariablePopoverContentProps {
  fieldEntry: NodeEntry<NonSigFieldElement | DynamicBlockElement>;
  variable: VariablePopoverVariable;
  wasSentForSignature: boolean;
}

export function IncompleteVariablePopoverContent({
  fieldEntry: [fieldElement, path],
  variable,
  wasSentForSignature,
}: IncompleteVariablePopoverContentProps): JSX.Element | null {
  const editor = useEditor();
  const { documentId } = useDocumentParams();
  const { classes } = useStyles();
  const {
    fieldsInJSON: { inChildren },
  } = useGetFieldsInJSON();
  const variableTypeString = getVariableVariantDisplayString(variable.variant);
  const { count = 0, locations = [] } = inChildren[variable.nodeId] ?? {};
  const occurrenceNum = locations.findIndex(p => Path.equals(p, path)) + 1;
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);
  const dispatchFieldCopied = useDispatchSuccessSnack({ message: 'Field copied to clipboard!' });

  if (isDynamicBlockElement(fieldElement)) {
    const { selectedChoice } = getSelectedChoice(variable, fieldElement);
    const copy = selectedChoice
      ? `Editing content for "${selectedChoice.optionText}"`
      : `Select a choice to edit ${variable.label}`;

    return (
      <div className={classes.wrapper}>
        <div className={classes.section}>
          <AlineTooltip arrow placement="top" title={variableTypeString}>
            <div>
              <IconCircle
                icon={
                  <FieldIcon
                    className={classes.icon}
                    fieldVariant={variableVariantToFieldVariant(variable.variant)}
                  />
                }
              />
            </div>
          </AlineTooltip>
          <Typography noWrap>{copy}</Typography>
        </div>
        <Spacer className={classes.spacer} />
        <div className={classes.buttons}>
          <ViewInOutlineButton variable={variable} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.section}>
        {variable.integrationLink?.integration && variable.integrationLink?.attributePath && (
          <CrmIntegrationIconWithTooltip
            attributePath={variable.integrationLink.attributePath}
            integration={variable.integrationLink.integration}
          />
        )}
        <AlineTooltip arrow placement="top" title={variableTypeString}>
          <div>
            <IconCircle
              icon={
                <FieldIcon
                  className={classes.icon}
                  fieldVariant={variableVariantToFieldVariant(variable.variant)}
                />
              }
            />
          </div>
        </AlineTooltip>
        <Typography noWrap>{variable.label}</Typography>
      </div>
      <Spacer className={classes.spacer} />
      <div className={classes.buttons}>
        <ViewInOutlineButton variable={variable} />
        <EditVariableButton variable={variable} wasSentForSignature={wasSentForSignature} />
        <AlineTooltip arrow placement="top" title="Copy variable to clipboard">
          <div>
            <IconButton
              size="small"
              onClick={async () => {
                const isCopied = await ClientTransforms.copyToClipboard(editor, {
                  at: path,
                  documentId,
                });
                if (isCopied) dispatchFieldCopied();
              }}
            >
              <Copy className={classes.icon} />
            </IconButton>
          </div>
        </AlineTooltip>
        <OccurrenceNavigation
          text={`${occurrenceNum}/${count}`}
          onClickDown={() => {
            ClientTransforms.navigateToNextDynamicElement(editor, {
              direction: 'after',
              match: n => n.fieldId === variable.nodeId,
              rWHeightPx,
              rWOffsetPx,
            });
          }}
          onClickUp={() => {
            ClientTransforms.navigateToNextDynamicElement(editor, {
              direction: 'before',
              match: n => n.fieldId === variable.nodeId,
              rWHeightPx,
              rWOffsetPx,
            });
          }}
        />
      </div>
    </div>
  );
}

function OccurrenceNavigation({
  onClickDown,
  onClickUp,
  text,
}: {
  onClickDown: () => void;
  onClickUp: () => void;
  text: string;
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.moveButtons}>
      <AlineTooltip arrow placement="top" title="Jump to previous occurrence">
        <div>
          <IconButton size="small" onClick={onClickUp}>
            <ChevronUp className={classes.icon} />
          </IconButton>
        </div>
      </AlineTooltip>
      <Typography noWrap color="textSecondary">
        {text}
      </Typography>
      <AlineTooltip arrow placement="top" title="Jump to next occurrence">
        <div>
          <IconButton size="small" onClick={onClickDown}>
            <ChevronDown className={classes.icon} />
          </IconButton>
        </div>
      </AlineTooltip>
    </div>
  );
}

function getSelectedChoice(
  variable: VariablePopoverVariable,
  dynamicBlock: DynamicBlockElement
): {
  selectedChoice: AnswerOptionSubset | null;
  selectedTrigger: Trigger | null;
} {
  const selectedTrigger = Queries.getSelectedTrigger(dynamicBlock);
  if (!selectedTrigger)
    return {
      selectedChoice: null,
      selectedTrigger,
    };

  const selectedChoiceId = selectedTrigger.conditions[0].answerOptionChoiceId;
  const selectedChoice =
    variable.answerOptions.options.find(option => option.choiceRefId === selectedChoiceId) ?? null;

  return { selectedChoice, selectedTrigger };
}

function EditVariableButton({
  variable,
  wasSentForSignature,
}: {
  variable: VariablePopoverVariable;
  wasSentForSignature: boolean;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();

  if (wasSentForSignature) return null;

  return (
    <AlineTooltip arrow placement="top" title="Edit variable">
      <div>
        <IconButton
          size="small"
          onClick={() => {
            navigate(
              getPathWithDrawerSearchParam(
                link.DocumentDetailFields({ documentId }),
                window.location.search,
                params.rightPanel.editFieldId,
                variable.id
              ),
              { replace: true }
            );
            openDrawer();
          }}
        >
          <SquarePen className={classes.icon} />
        </IconButton>
      </div>
    </AlineTooltip>
  );
}

function ViewInOutlineButton({ variable }: { variable: VariablePopoverVariable }) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { tabId } = useRequiredEditorRuntime();
  const { openDrawer } = useGlobalDrawer();

  return (
    <AlineTooltip arrow placement="top" title="View in outline">
      <div>
        <IconButton
          size="small"
          onClick={() => {
            navigate(
              getPathWithDrawerSearchParam(
                link.DocumentDetailFields({ documentId }),
                window.location.search,
                params.rightPanel.editFieldId,
                null
              ),
              { replace: true }
            );
            openDrawer();
            focusVariableAfterOutlineSync(tabId, variable);
          }}
        >
          <Eye className={classes.icon} />
        </IconButton>
      </div>
    </AlineTooltip>
  );
}

function focusVariableAfterOutlineSync(tabId: UUID, variable: VariablePopoverVariable): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setVariablePanelFocusedField(tabId, variable);
    });
  });
}
